import { useEffect, useState } from 'react';
import { createPortal } from 'react-dom';
import { amount, money, unitPrice } from './ui';

/**
 * A printed order: one A4 sheet with only what travels with the goods — who receives them,
 * when, what (with quantities and prices), a mini program code to open the order, and room
 * to sign. Plain black lines and square corners, like a paper form; nothing from the screen
 * (steps, progress, history) is printed.
 */
export interface PrintLine {
  name: string;
  spec?: string;
  unit: string;
  quantity: string | number;
  unitPrice: string | number;
  amount: string | number;
  /** What goes out with the product, e.g. "24 口盖 ×100、垫片 ×100". */
  accessories?: string;
}
export interface PrintData {
  /** 送货单 / 采购单 */
  title: string;
  /** This enterprise, printed at the top. */
  issuer: string;
  /** The other party, labelled 收货单位 or 供货单位. */
  partyLabel: string;
  party: string;
  number: string;
  orderDate: string;
  deliveryDate: string;
  lines: PrintLine[];
  note?: string;
  /** Image URL of the mini program code; omitted when there is none. */
  qr?: string | null;
  qrCaption?: string;
  /** Shown where the code would be, e.g. when the customer has already signed. */
  qrNote?: string;
  /** Material price adjustment on top of the quoted goods, with the reason printed in full. */
  adjust?: { amount: number; rate: string; reasons: string[] } | null;
  /** The driver's pickup code (sales), printed where the shipper would otherwise sign. */
  pickupQr?: string | null;
}

const day = (v: string) => {
  const d = new Date(v);
  return `${d.getFullYear()}年${d.getMonth() + 1}月${d.getDate()}日`;
};

/**
 * Prints a sheet: renders it (hidden on screen), waits for its images, opens the print dialog,
 * and removes it afterwards. While it is mounted the page itself is hidden from the printer.
 */
export function usePrintSheet() {
  const [doc, setDoc] = useState<PrintData | null>(null);
  useEffect(() => {
    if (!doc) return;
    document.body.classList.add('printing-sheet');
    let cancelled = false;
    const images = [...document.querySelectorAll<HTMLImageElement>('.print-sheet img')];
    Promise.all(images.map((img) => img.decode().catch(() => undefined))).then(() => {
      if (!cancelled) window.print();
    });
    const done = () => setDoc(null);
    window.addEventListener('afterprint', done);
    return () => {
      cancelled = true;
      window.removeEventListener('afterprint', done);
      document.body.classList.remove('printing-sheet');
    };
  }, [doc]);
  return {
    print: setDoc,
    sheet: doc ? createPortal(<PrintSheet {...doc} />, document.body) : null,
  };
}

const cell: React.CSSProperties = { border: '1px solid #000', padding: '6px 8px' };

function PrintSheet(p: PrintData) {
  const total = p.lines.reduce((s, l) => s + Number(l.amount), 0) + (p.adjust?.amount ?? 0);
  const quantity = p.lines.reduce((s, l) => s + Number(l.quantity), 0);
  const sameUnit = p.lines.every((l) => l.unit === p.lines[0]?.unit);
  return (
    <div className="print-sheet">
      {/* Company and title centred like a paper form; the code sits in the right margin. */}
      <div style={{ position: 'relative', minHeight: '35mm', textAlign: 'center' }}>
        <div style={{ fontSize: '13pt', fontWeight: 700, paddingTop: '2mm' }}>{p.issuer}</div>
        <div style={{ fontSize: '22pt', fontWeight: 700, letterSpacing: '0.5em', marginTop: 8 }}>
          {p.title}
        </div>
        {(p.qr || p.qrNote) && (
          <div
            style={{
              position: 'absolute',
              top: 0,
              right: 0,
              width: '28mm',
              textAlign: 'center',
              fontSize: '8pt',
            }}
          >
            {p.qr ? (
              <>
                <img
                  src={p.qr}
                  alt=""
                  style={{ width: '28mm', height: '28mm', display: 'block' }}
                />
                <div style={{ marginTop: 3 }}>{p.qrCaption}</div>
              </>
            ) : (
              <div style={{ border: '1px solid #000', padding: '9mm 2mm' }}>{p.qrNote}</div>
            )}
          </div>
        )}
      </div>

      {/* The facts people look for first, in one box. */}
      <table
        style={{ width: '100%', borderCollapse: 'collapse', marginTop: 10, fontSize: '10.5pt' }}
      >
        <tbody>
          <tr>
            <td style={{ ...cell, width: '13%' }}>{p.partyLabel}</td>
            <td style={{ ...cell, width: '37%', fontWeight: 700 }}>{p.party}</td>
            <td style={{ ...cell, width: '13%' }}>单号</td>
            <td style={{ ...cell, width: '37%' }}>{p.number}</td>
          </tr>
          <tr>
            <td style={cell}>交付日期</td>
            <td style={{ ...cell, fontWeight: 700 }}>{day(p.deliveryDate)}</td>
            <td style={cell}>下单日期</td>
            <td style={cell}>{day(p.orderDate)}</td>
          </tr>
          {p.note && (
            <tr>
              <td style={cell}>备注</td>
              <td style={cell} colSpan={3}>
                {p.note}
              </td>
            </tr>
          )}
        </tbody>
      </table>

      <table
        style={{ width: '100%', borderCollapse: 'collapse', marginTop: 12, fontSize: '10.5pt' }}
      >
        <thead>
          <tr>
            {['序号', '商品名称', '规格', '单位', '数量', '单价', '金额'].map((h, i) => (
              <th
                key={h}
                style={{
                  ...cell,
                  fontWeight: 700,
                  textAlign: i >= 4 ? 'right' : i === 0 || i === 3 ? 'center' : 'left',
                  width: ['7%', '25%', '29%', '7%', '10%', '10%', '12%'][i],
                  whiteSpace: 'nowrap',
                }}
              >
                {h}
              </th>
            ))}
          </tr>
        </thead>
        <tbody>
          {p.lines.map((l, i) => (
            <tr key={i}>
              <td style={{ ...cell, textAlign: 'center' }}>{i + 1}</td>
              <td style={cell}>
                {l.name}
                {l.accessories && (
                  <div style={{ fontSize: '8pt', color: '#666' }}>随附：{l.accessories}</div>
                )}
              </td>
              <td style={cell}>{l.spec}</td>
              <td style={{ ...cell, textAlign: 'center' }}>{l.unit}</td>
              <td style={{ ...cell, textAlign: 'right', fontWeight: 700 }}>{amount(l.quantity)}</td>
              <td style={{ ...cell, textAlign: 'right' }}>{unitPrice(l.unitPrice)}</td>
              <td style={{ ...cell, textAlign: 'right' }}>{money(l.amount)}</td>
            </tr>
          ))}
          {p.adjust && (
            <tr>
              <td style={{ ...cell, fontSize: '9pt' }} colSpan={6}>
                原料调整 {p.adjust.rate}：{p.adjust.reasons.join('；')}
              </td>
              <td style={{ ...cell, textAlign: 'right' }}>
                {p.adjust.amount > 0 ? '+' : '-'}
                {money(Math.abs(p.adjust.amount))}
              </td>
            </tr>
          )}
          <tr>
            <td style={{ ...cell, textAlign: 'center', fontWeight: 700 }} colSpan={4}>
              合计
            </td>
            <td style={{ ...cell, textAlign: 'right', fontWeight: 700 }}>
              {/* Quantities only add up when every line uses the same unit. */}
              {sameUnit ? `${amount(quantity)}` : ''}
            </td>
            <td style={cell} />
            <td style={{ ...cell, textAlign: 'right', fontWeight: 700 }}>{money(total)}</td>
          </tr>
        </tbody>
      </table>

      {/* The driver confirms by scanning instead of signing; the receiver still signs on paper. */}
      <div
        style={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: p.pickupQr ? 'flex-end' : 'baseline',
          marginTop: p.pickupQr ? 18 : 28,
          fontSize: '10.5pt',
          gap: '6mm',
        }}
      >
        {p.pickupQr ? (
          <div style={{ display: 'flex', alignItems: 'center', gap: '3mm' }}>
            <img
              src={p.pickupQr}
              alt=""
              style={{ width: '22mm', height: '22mm', display: 'block' }}
            />
            <div style={{ fontSize: '9pt', lineHeight: 1.7 }}>
              <div style={{ fontWeight: 700, fontSize: '10.5pt' }}>司机扫码确认提货</div>
              <div>微信扫一扫，无需注册</div>
            </div>
          </div>
        ) : (
          <span>发货人：________________</span>
        )}
        <span>收货人签字：________________</span>
        <span>收货日期：______年____月____日</span>
      </div>
      <div style={{ marginTop: 18, fontSize: '8pt', textAlign: 'right' }}>
        打印于 {new Date().toLocaleString('zh-CN', { hour12: false })} · 联仓通
      </div>
    </div>
  );
}
